import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Typography, Theme, createStyles } from '@material-ui/core';
import { DataGrid } from 'components';
import { ColumnConfig, DataConnection } from 'hooks';
import { toDateTimeString } from 'helpers';
import {
  SpecialProjectsFilter,
  SpecialProjectsPropsFragment,
  useSpecialProjectsQuery,
} from 'schema/serverTypes';
import { Link } from 'react-router-dom';
import { DataGridProps } from 'components/DataGrid/types';

import { toast } from 'react-toastify';
import { SpecialProjectStatuses } from './SpecialProjectStatuses';
import { SpecialProjectSearch } from './SpecialProjectSearch';
import { useSpecialProjectsQueryVariables } from '../../hooks/useSpecialProjectsQueryVariables';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    nameContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    link: {
      color: theme.palette.primary.main,
    },
    statusAlarm: {
      color: theme.palette.error.main,
    },
    statusSuccess: {
      color: theme.palette.success.main,
    },
    statusWarning: {
      color: theme.palette.warning.main,
    },
    dateOfDeath: {
      textAlign: 'center',
    },
    fundImage: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      overflow: 'hidden',
      margin: '0 auto',
      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      },
    },
    fundImagePlaceholder: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: '#e0e0e0',
      margin: '0 auto',
    },
  })
);

const wrapTableCellWithLink = (project: SpecialProjectsPropsFragment, cell: React.ReactNode) => (
  <Link to={`/special-projects/manage/${project.id}`}>{cell}</Link>
);

const getDataKey = (project: SpecialProjectsPropsFragment) => project.id;

const formatAmount = (amount: number) => {
  return new Intl.NumberFormat('ru-RU').format(amount);
};

const formatFundingAmount = (project: SpecialProjectsPropsFragment) => {
  const collectedAmount = project.account.amount;
  const requiredAmount =
    project.account.__typename === 'SpecialProjectAccount' ? project.account.requiredAmount : null;

  const formattedCollected = formatAmount(collectedAmount);
  const formattedRequired = requiredAmount ? formatAmount(requiredAmount) : 'не задана';

  return `${formattedCollected} / ${formattedRequired}`;
};

export const SpecialProjectsGrid = () => {
  const classes = useStyles();

  const config = useMemo(() => {
    const columns: ColumnConfig<SpecialProjectsPropsFragment>[] = [
      {
        id: 'fund',
        header: 'Фонд',
        align: 'center',
        style: { width: '5%' },
        render: (project) => {
          if (project.logo?.url) {
            return (
              <div className={classes.fundImage}>
                <img src={project.logo.url} alt={project.title} />
              </div>
            );
          }
          return <div className={classes.fundImagePlaceholder} />;
        },
      },
      {
        id: 'status',
        header: 'Статус',
        align: 'center',
        style: { width: '5%' },
        render: (project) => <SpecialProjectStatuses project={project} />,
      },
      {
        id: 'title',
        header: 'Название',
        style: { width: '15%' },
        render: (project: SpecialProjectsPropsFragment) => {
          return (
            <div className={classes.nameContainer}>
              {wrapTableCellWithLink(
                project,
                <Typography variant="body1" className={classes.link}>
                  {project.title}
                </Typography>
              )}
            </div>
          );
        },
      },
      {
        id: 'city',
        header: 'Город',
        style: { width: '10%' },
        render: (project: SpecialProjectsPropsFragment) => project.city,
      },
      {
        id: 'fundingAmount',
        header: 'Сумма сбора',
        style: { width: '10%' },
        render: (project: SpecialProjectsPropsFragment) => formatFundingAmount(project),
      },
      {
        id: 'start',
        header: 'Дата начала',
        style: { width: '10%' },
        render: (project: SpecialProjectsPropsFragment) => {
          const date = new Date(project.start as string);
          return <div>{toDateTimeString(date)}</div>;
        },
      },
    ];
    return {
      columns,
      rowsPerPage: 50,
    };
  }, [classes]);

  const {
    onSort,
    onSearch,
    onPage,
    variables,
    extractPagedData,
    columns,
    sortDirection,
    sortBy,
    onIncludeClosedOrCancelled,
    onSearchCity,
    onSpecialProjectsFilterChange,
  } = useSpecialProjectsQueryVariables(config);

  const { data, loading, error } = useSpecialProjectsQuery({
    variables: {
      ...variables,
      filter: variables.filter as SpecialProjectsFilter,
    },
  });

  if (error) {
    toast.error('Ошибка загрузки спецпроектов');
    return null;
  }

  const entities = data?.specialProjects as DataConnection<SpecialProjectsPropsFragment>;
  const {
    data: specialProjects,
    count,
    startCursor,
    endCursor,
    page,
    rowsPerPage,
  } = extractPagedData(entities);

  const gridProps: DataGridProps<SpecialProjectsPropsFragment> = {
    data: specialProjects,
    onSort,
    onPage,
    onSearch,
    columns,
    getDataKey,
    onIncludeClosedOrCancelled,
    onSearchCity,
    onSpecialProjectsFilterChange,
    count,
    startCursor: startCursor,
    endCursor: endCursor,
    page,
    searchComponent: SpecialProjectSearch,
    rowsPerPage,
    sortDirection,
    sortBy,
    loading,
  };

  return (
    <>
      <DataGrid<SpecialProjectsPropsFragment> {...gridProps} />
    </>
  );
};
